import type { CellClassParams, ColDef, GridOptions, ICellRendererParams } from "ag-grid-community"
import { GearSix, Package, Storefront, TShirt } from "@phosphor-icons/react"

import { Button } from "~/components/design-system/components"
import ProductImage from "~/components/reusable/ProductImage"
import { Tooltip } from "~/components/reusable/Tooltip"
import { TableColumnGroups } from "~/constants/tableColumnGroups"
import { SETTING_FIELDS, type SettingField } from "~/utils/allocationLineSettings"
import { pluralize } from "~/utils/formatting"
import { cn } from "~/utils/helpers"
import type { ListCounts } from "./productSettingsModel"
import { formatUnits, SETTING_FIELD_META, type DialogTab } from "./productSettingFields"
import type { GroupBy, Row, RowScope } from "./productRows"

/**
 * The dialog opens on the variant at every store: settings are global. Under a store or package
 * group it starts on that group's stores.
 */
export type GridContext = { open: (variantId: string, tab: DialogTab, scope?: RowScope) => void }

/** A row's counts; null on placeholders and on a store not expanded yet. */
function rowCounts(row: Row | undefined): ListCounts | null {
  if (!row || row.kind === "placeholder" || (row.kind === "group" && row.pending)) return null
  return row.counts
}

function settingsTooltip(scope: RowScope | undefined): string {
  const fields = "store exclusions, minimum display quantity, maximum stock and order multiple"
  if (scope === undefined) return `Set ${fields} for the whole variant`
  return `Set ${fields} for the whole variant, starting with this ${scope.kind}'s stores`
}

/** What a setting column sorts on: the count of cells set. */
function settingSortValue(row: Row | undefined, field: SettingField): number {
  return rowCounts(row)?.[field] ?? 0
}

function totalSortValue(row: Row | undefined, field: "totalMin" | "totalMax"): number {
  return rowCounts(row)?.[field] ?? Number.POSITIVE_INFINITY
}

/** What copying the first column gives: the variant id or the group's name. */
function clipboardName(row: Row | undefined): string {
  if (row?.kind === "group") return row.label
  if (row?.kind === "variant") return row.listing.variantId
  return ""
}

/** The name a row sorts by in the first column. */
function nameSortValue(row: Row | undefined): string {
  if (row?.kind === "group") return row.label
  if (row?.kind === "variant") return row.listing.name
  return ""
}

/** A row's figure for one setting column: the store x size cells set. */
function settingText(row: Row, field: SettingField): string {
  const counts = rowCounts(row)
  if (counts === null) return ""
  const count = counts[field].toLocaleString("en")
  if (row.kind !== "variant" || field === "excluded") return count
  const allStores = row.summary.variantLevel?.[field] ?? null
  return allStores === null ? count : `${count} · all ${allStores}`
}

function settingTone(row: Row, field: SettingField): string {
  const counts = rowCounts(row)
  if (counts === null) return "text-fg-muted"
  const allStores =
    row.kind === "variant" && field !== "excluded"
      ? (row.summary.variantLevel?.[field] ?? null)
      : null
  return counts[field] > 0 || allStores !== null
    ? `${SETTING_FIELD_META[field].tone} font-semibold`
    : "text-fg-muted"
}

const GROUP_ICON = { package: Package, product: TShirt, store: Storefront } as const

function NameCell({ data, context }: ICellRendererParams<Row, unknown, GridContext>) {
  if (!data) return null
  if (data.kind === "total") {
    return <span className="label-sm">{pluralize(data.variantCount, "variant")}</span>
  }
  if (data.kind === "placeholder") {
    return <span className="paragraph-xs text-fg-muted">Loading variants…</span>
  }
  if (data.kind === "group") {
    const Icon = GROUP_ICON[data.groupBy]
    return (
      <span className="flex min-w-0 items-center gap-2">
        <Icon className="size-4 shrink-0 text-fg-muted" />
        <span className="label-sm truncate">{data.label}</span>
        <span className="paragraph-xs whitespace-nowrap text-fg-muted">
          {pluralize(data.variantCount, "variant")}
          {!data.pending && ` · ${pluralize(data.counts.sizes, "SKU")}`}
        </span>
      </span>
    )
  }
  return (
    <span className="flex min-w-0 items-center gap-2">
      <ProductImage url={data.listing.imageUrl} alt={data.listing.name} width={20} />
      <span className="paragraph-sm truncate font-medium">{data.listing.productName}</span>
      {/* Shows on hover or row focus; at rest it takes no room from the name. */}
      <span className="hidden shrink-0 [.ag-row-focus_&]:block [.ag-row-hover_&]:block">
        <Tooltip title={settingsTooltip(data.scope)}>
          <Button
            size="xs"
            variant="neutral"
            className="filled-surface-button gap-1.5"
            onClick={(event) => {
              event.stopPropagation()
              context.open(data.listing.variantId, "stores", data.scope)
            }}
          >
            <GearSix />
            Settings
          </Button>
        </Tooltip>
      </span>
    </span>
  )
}

function squareTooltip(field: SettingField, count: number) {
  const { squareLabel, squareVerb } = SETTING_FIELD_META[field]
  return `${squareLabel}: ${pluralize(count, "cell")} ${squareVerb}`
}

/** Four count squares (exclusions, min, max, order multiple); on a variant each opens that tab. */
function ProductSettingsCell({ data, context }: ICellRendererParams<Row, unknown, GridContext>) {
  const counts = rowCounts(data)
  if (!data || counts === null) return null
  return (
    <span className="flex h-full w-full items-center justify-center gap-1">
      {SETTING_FIELDS.map((field) => {
        const count = counts[field]
        const className = cn(
          "inline-flex h-5 min-w-5 items-center justify-center rounded-sm border px-1 text-xs tabular-nums",
          count > 0
            ? `${SETTING_FIELD_META[field].squareTone} font-semibold`
            : "border-base text-fg-muted",
        )
        const label = squareTooltip(field, count)
        if (data.kind !== "variant") {
          return (
            <Tooltip key={field} title={label}>
              <span className={className} aria-label={label}>
                {count}
              </span>
            </Tooltip>
          )
        }
        return (
          <Tooltip key={field} title={label}>
            <Button
              size="2xs"
              variant="transparent"
              className={cn(className, "rounded-sm")}
              aria-label={label}
              onClick={() =>
                context.open(data.listing.variantId, SETTING_FIELD_META[field].tab, data.scope)
              }
            >
              {count}
            </Button>
          </Tooltip>
        )
      })}
    </span>
  )
}

const rightAligned = "text-right tabular-nums"

type ProductColumn = { group: TableColumnGroups; def: ColDef<Row> & { colId: string } }

const settingColumn = (field: SettingField, width: number): ProductColumn => ({
  group: TableColumnGroups.AllocationInfo,
  def: {
    colId: field,
    headerName: SETTING_FIELD_META[field].header,
    width,
    headerClass: "ag-right-aligned-header",
    valueGetter: ({ data }) => (data ? settingText(data, field) : ""),
    comparator: (_a, _b, nodeA, nodeB) =>
      settingSortValue(nodeA.data, field) - settingSortValue(nodeB.data, field),
    cellClass: ({ data }: CellClassParams<Row>) =>
      cn(
        rightAligned,
        data && settingTone(data, field),
        data?.kind === "variant" && "cursor-pointer",
      ),
  },
})

const totalColumn = (field: "totalMin" | "totalMax", headerName: string): ProductColumn => ({
  group: TableColumnGroups.AllocationInfo,
  def: {
    colId: field,
    comparator: (_a, _b, nodeA, nodeB) =>
      totalSortValue(nodeA.data, field) - totalSortValue(nodeB.data, field),
    headerName,
    width: 100,
    headerClass: "ag-right-aligned-header",
    cellClass: rightAligned,
    valueGetter: ({ data }) => {
      if (!data) return ""
      const counts = rowCounts(data)
      return counts === null ? "" : formatUnits(counts[field])
    },
  },
})

/** The list's columns after the name, each with the column chooser group it sits in. */
export const COLUMNS: ProductColumn[] = [
  {
    group: TableColumnGroups.ProductInformation,
    def: {
      colId: "variant",
      headerName: "Variant",
      width: 170,
      valueGetter: ({ data }) => (data?.kind === "variant" ? data.listing.variantName : ""),
    },
  },
  {
    group: TableColumnGroups.ProductInformation,
    def: {
      colId: "class",
      headerName: "Class",
      width: 130,
      valueGetter: ({ data }) => (data?.kind === "variant" ? (data.listing.category ?? "") : ""),
    },
  },
  {
    group: TableColumnGroups.ProductInformation,
    def: {
      colId: "packages",
      headerName: "In packages",
      width: 170,
      valueGetter: ({ data }) => {
        if (data?.kind !== "variant") return ""
        const [first, ...rest] = data.packageNames
        return rest.length === 0 ? (first ?? "") : `${first} +${rest.length}`
      },
      tooltipValueGetter: ({ data }) =>
        data?.kind === "variant" && data.packageNames.length > 1
          ? data.packageNames.join(", ")
          : undefined,
    },
  },
  {
    group: TableColumnGroups.AllocationInfo,
    def: {
      colId: "productSettings",
      headerName: "Product settings",
      width: 140,
      cellRenderer: ProductSettingsCell,
      headerClass: "[&_.ag-header-cell-label]:justify-center",
      comparator: (_a, _b, nodeA, nodeB) => {
        const sum = (row: Row | undefined) =>
          SETTING_FIELDS.reduce((total, field) => total + settingSortValue(row, field), 0)
        return sum(nodeA.data) - sum(nodeB.data)
      },
    },
  },
  {
    group: TableColumnGroups.ProductInformation,
    def: {
      colId: "sizes",
      headerName: "Sizes",
      width: 80,
      headerClass: "ag-right-aligned-header",
      cellClass: rightAligned,
      valueGetter: ({ data }) => rowCounts(data)?.sizes ?? "",
    },
  },
  settingColumn("minDisplayQty", 170),
  settingColumn("maxQty", 140),
  settingColumn("excluded", 140),
  settingColumn("orderMultiple", 120),
  totalColumn("totalMin", "Total min"),
  totalColumn("totalMax", "Total max"),
]

const GROUP_COLUMN_HEADER: Record<GroupBy, string> = {
  package: "Package / product",
  product: "Product / variant",
  store: "Store / variant",
  variant: "Variant",
}

export const autoGroupColumn = (groupBy: GroupBy): ColDef<Row> => ({
  headerName: GROUP_COLUMN_HEADER[groupBy],
  comparator: (_a, _b, nodeA, nodeB) =>
    nameSortValue(nodeA.data).localeCompare(nameSortValue(nodeB.data)),
  minWidth: 300,
  flex: 1,
  // A group's count changes with the filters; a changed value is what makes AG Grid redraw the cell.
  valueGetter: ({ data }) =>
    data?.kind === "group" || data?.kind === "total"
      ? `${data.id} ${data.variantCount} ${data.counts.sizes}`
      : data?.id,
  cellRendererParams: { suppressCount: true, innerRenderer: NameCell },
})

export const GRID_OPTIONS: GridOptions<Row> = {
  treeData: true,
  getDataPath: (row) => row.path,
  getRowId: ({ data }) => data.id,
  groupDefaultExpanded: 0,
  suppressAggFuncInHeader: true,
  // Edits only update row data; the tree, sort and filter stay as they are.
  suppressModelUpdateAfterUpdateTransaction: true,
  // The first column's value is an internal row path; copy what a planner can search with.
  processCellForClipboard: ({ column, node, value }) =>
    column.getColId() === "ag-Grid-AutoColumn" ? clipboardName(node?.data) : value,
  rowClassRules: {
    "bg-subtle!": ({ data }) => data?.kind === "total",
  },
}
